import React from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useDashboardAccessNotifications } from '@/hooks/useDashboardAccessNotifications';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export const DashboardAccessCodesPanel: React.FC = () => {
  const { notifications, unreadCount } = useDashboardAccessNotifications();

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const getTimeLeft = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Expired';
    
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    
    return `${minutes}m ${seconds}s left`;
  };

  const getDashboardLabel = (type: string) => {
    return type === 'consultant' ? 'Consultant Dashboard' : 'SDR Dashboard';
  };

  if (!notifications || notifications.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Dashboard Access Codes</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground text-center">
            No active codes
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className="flex flex-col items-start gap-2 p-4 cursor-pointer"
                onClick={() => handleCopyCode(notification.code)}
              >
                <div className="flex items-center justify-between w-full">
                  <span className="font-mono text-2xl font-bold">
                    {notification.code}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    Click to copy
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground space-y-1 w-full">
                  <div className="flex justify-between">
                    <span>{getDashboardLabel(notification.dashboard_type)}</span>
                    <span className="font-medium">{getTimeLeft(notification.expires_at)}</span>
                  </div>
                  <div className="text-xs opacity-70">
                    Requested {formatDistanceToNow(new Date(notification.requested_at), { addSuffix: true })}
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
